import { Button, Flex, Popover, Typography } from "antd";
import { useAntTheme } from "hooks/useAntTheme/";
import { useTranslate } from "#src/hooks";
import {
  HandsPrayingIcon,
  LightningIcon,
  MegaphoneIcon,
  SmileyIcon,
  SmileyMeltingIcon,
  SmileySadIcon,
  SmileyWinkIcon,
} from "@phosphor-icons/react";
import { VoiceoverPopoverTitle } from "../VoiceoverPopoverTitle";
import styled from "styled-components";
import React from "react";
import {
  getEmoColor,
  getSentenceForToken,
  getSentenceFromTokenToEnd,
  hasSpaceBefore,
  renderSentence,
  WordToken,
} from "../../../../helpers/scriptHelpers";

const TokenTrigger = styled.span`
  display: inline;
  cursor: pointer;
  font: inherit;
`;

const EmoHighlight = styled.span<{ $bgColor: string }>`
  background-color: ${({ $bgColor }) => $bgColor};
  border-radius: 2px;
  padding: 0 1px;
`;

export const EmotionPopover = ({
  tokenIdx,
  tokens,
  isOpen,
  onOpenChange,
  onEdit,
  onRemove,
}: {
  tokenIdx: number;
  tokens: WordToken[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (
    tokenIdx: number,
    emoId: string,
    currentEmoId: string | undefined
  ) => void;
  onRemove: (tokenIdx: number) => void;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const getEmoHighlightColor = (emoId: string) =>
    `${getEmoColor(emoId) ?? theme.colorPrimary}`;

  const emotionsList = [
    { id: "happily", icon: <SmileyIcon /> },
    { id: "sadly", icon: <SmileySadIcon /> },
    { id: "excitedly", icon: <LightningIcon /> },
    { id: "whispers", icon: <MegaphoneIcon /> },
    { id: "sarcastically", icon: <SmileyMeltingIcon /> },
    { id: "proudly", icon: <SmileyWinkIcon /> },
    { id: "hopefully", icon: <HandsPrayingIcon /> },
  ];

  const token = tokens[tokenIdx];

  const previousToken = tokens[tokenIdx - 1];
  const doesNeedSpaceBefore = !!token && hasSpaceBefore(token, tokenIdx);
  // spaces inside highlighted emo
  const isSpaceBeforeHighlighted = doesNeedSpaceBefore && !!token.emoId;
  // spaces outside highlighted emo or between 2 emo highlights
  const isSpaceBeforeUncolored =
    (doesNeedSpaceBefore && !token.emoId) ||
    (previousToken?.emoId !== token?.emoId && token?.spaceBefore !== false);

  return (
    <>
      {isSpaceBeforeUncolored && " "}
      <Popover
        open={isOpen}
        onOpenChange={(open) => onOpenChange(open)}
        trigger="click"
        placement="top"
        title={
          <VoiceoverPopoverTitle
            hasItem={!!token.emoId}
            itemLabel={
              token.emoId ? (
                <span
                  style={{
                    textTransform: "none",
                    fontWeight: 400,
                    backgroundColor: `${getEmoHighlightColor(token.emoId)}60`,
                  }}
                >
                  (
                  {t(
                    `components.templates.templatePreview.voiceoverNarration.emotions.${token.emoId}`
                  )}
                  )
                </span>
              ) : undefined
            }
            onDelete={token.emoId ? () => onRemove(tokenIdx) : undefined}
            addKey="emotions.addEmotion"
            editKey="emotions.editEmotion"
          />
        }
        content={
          <Flex
            vertical
            gap={10}
            style={{ minWidth: 120, maxWidth: 500, padding: 16 }}
          >
            <Flex
              gap={16}
              wrap="wrap"
              justify="center"
              style={{ paddingBottom: 10 }}
            >
              {emotionsList.map((emo) => (
                <Button
                  key={emo.id}
                  icon={React.cloneElement(emo.icon, {
                    size: 18,
                    color: getEmoHighlightColor(emo.id),
                  })}
                  shape="round"
                  onClick={() => onEdit(tokenIdx, emo.id, token.emoId)}
                >
                  <Typography.Text style={{ color: theme.colorText }}>
                    {getEmoColor(emo.id)
                      ? t(
                          `components.templates.templatePreview.voiceoverNarration.emotions.${emo.id}`
                        )
                      : emo.id}
                  </Typography.Text>
                </Button>
              ))}
            </Flex>
            <Typography.Text
              type="secondary"
              style={{
                whiteSpace: "normal",
              }}
            >
              {token.emoId
                ? renderSentence(getSentenceForToken(tokens, tokenIdx))
                : renderSentence(getSentenceFromTokenToEnd(tokens, tokenIdx))}
            </Typography.Text>
          </Flex>
        }
        styles={{ body: { padding: 0 } }}
      >
        <TokenTrigger role="button" tabIndex={0}>
          {token.emoId ? (
            <EmoHighlight $bgColor={`${getEmoHighlightColor(token.emoId)}60`}>
              {isSpaceBeforeHighlighted && " "}
              {token.word}
            </EmoHighlight>
          ) : (
            token.word
          )}
        </TokenTrigger>
      </Popover>
    </>
  );
};
